import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from "react-router-dom";

import AuthPage from './AuthPage';
import Cart from './Cart';
import Header from './Header';
import Carousel from './Carousel';
import CategoryGrid from './CategoryGrid';
import DealsSection from './DealsSection';
import ProductDetails from './ProductDetails';
import CategoryPage from './CategoryPage';
import Checkout from './Checkout';
import SavedAddresses from './SavedAddresses';
import OrdersDashboard from './OrdersDashboard';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop'; // 🌟 Ensure this is imported

// Removed redundant 'categories' param
function HomePage({ user, accessToken, selectedType, onAddToCart, onRequireLogin, productTypes }) {
  return (
    <main className="flex flex-1 justify-center py-5 sm:py-8 lg:py-12">
      <div className="layout-content-container flex flex-col max-w-7xl flex-1 px-4 sm:px-10 lg:px-20 gap-12 w-full">
        <Carousel />
        <CategoryGrid productTypes={productTypes} />
        <DealsSection
          user={user}
          accessToken={accessToken}
          selectedType={selectedType}
          productTypes={productTypes} 
          onAddToCart={onAddToCart}
          onRequireLogin={onRequireLogin}
        />
      </div>
    </main>
  );
}

function App() {
  const navigate = useNavigate();

  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')) || null; } catch { return null; }
  });
  const [cartCount, setCartCount] = useState(0);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken') || null);
  const [selectedType, setSelectedType] = useState('All');
  const [productTypes, setProductTypes] = useState([]);

  // 🌟 UPDATE THIS EXISTING USEEFFECT BLOCK
  useEffect(() => {
    // 1. Force the browser to naturally track and restore previous positions when hitting 'Back'
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }

    // 2. Your existing fetch logic
    fetch(`${process.env.REACT_APP_API_URL}/api/products/product-types/`)
      .then(res => res.json())
      .then(data => setProductTypes(data))
      .catch(() => setProductTypes([]));
  }, []);

  // CENTRALIZED HANDLER: Reusable across routes to sync application state with DB
  const refreshCart = useCallback(() => {
    if (!accessToken) {
      setCartCount(0);
      return Promise.resolve([]);
    }
    return fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    })
      .then(res => res.status === 401 ? null : res.json())
      .then(data => {
        if (!data) return [];
        const items = Array.isArray(data) ? data : (data?.results || []);
        setCartCount(items.reduce((sum, item) => sum + (item.quantity || 1), 0));
        return items;
      })
      .catch(() => {
        return [];
      });
  }, [accessToken]);

  // Synchronize cart count on initial load or auth transitions
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const handleAuthError = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    setUser(null);
    setAccessToken(null);
    setCartCount(0);
    navigate("/login");
  };

  const handleLogin = (data) => {
    setAccessToken(data.access);
    setUser({ logged_in: true });
    localStorage.setItem('accessToken', data.access);
    localStorage.setItem('user', JSON.stringify({ logged_in: true }));
    navigate("/");
  };

  const handleRegister = (data) => {
    setAccessToken(data.access);
    setUser({ logged_in: true });
    localStorage.setItem('accessToken', data.access);
    localStorage.setItem('user', JSON.stringify({ logged_in: true }));
    navigate("/");
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    if (!accessToken) {
      setUser(null);
      setAccessToken(null);
      setCartCount(0);
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL}/api/logout/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    })
      .finally(() => {
        setUser(null);
        setAccessToken(null);
        setCartCount(0);
        navigate("/");
      });
  };

  const handleOrderSuccess = () => {
    setCartCount(0);
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col bg-background-light text-gray-900 antialiased selection:bg-gray-200">
      
      {/* 🌟 INJECTED: Positioned here globally, it catches all routing changes */}
      <ScrollToTop />

      <Header
        onSignInClick={() => navigate("/login")}
        onCartClick={() => navigate("/cart")}
        user={user}
        onLogout={handleLogout}
        cartCount={cartCount}
      />

      <Routes>
        <Route path="/" element={
          <HomePage
            user={user}
            accessToken={accessToken}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            onAddToCart={refreshCart}
            onRequireLogin={() => navigate("/login")}
            productTypes={productTypes}
          />
        } />

        <Route path="/login" element={<AuthPage mode="login" onLogin={handleLogin} />} />
        <Route path="/signup" element={<AuthPage mode="register" onRegister={handleRegister} />} />
        
        <Route
          path="/product/:id"
          element={
            <ProductDetails
              user={user}
              accessToken={accessToken}
              onAddToCart={refreshCart}               
              onRequireLogin={() => navigate("/login")}
              onAuthError={handleAuthError}          
            />
          }
        />

        <Route path="/category/:typeId" element={
          <CategoryPage
            user={user}
            accessToken={accessToken}
            onAddToCart={refreshCart}
            onRequireLogin={() => navigate("/login")}
            onAuthError={handleAuthError}
            productTypes={productTypes} 
          />
        } />

        <Route
          path="/cart"
          element={
            <Cart
              accessToken={accessToken}
              refreshCart={refreshCart}
              onAuthError={handleAuthError}
            />
          }
        />

        <Route
          path="/checkout"
          element={
            <Checkout
              user={user}
              accessToken={accessToken}
              onOrderSuccess={handleOrderSuccess}
              refreshCart={refreshCart}
            />
          }
        />
        <Route
          path="/addresses"
          element={<SavedAddresses accessToken={accessToken} />}
        />
        <Route path="/orders" element={<OrdersDashboard accessToken={accessToken} />} />
      </Routes>

      <Footer />
    </div>
  );
}

export default App;